import { eventEmitter } from '@/event-bus'
import i18n from '@/i18n/i18n'
import PaymentService from '@/services/admin/PaymentService'
import { errorMessage } from '@/utilities/helpers'

const { t } = i18n.global

export const namespaced = true

export const state = {
  payments: null,
  payment: {},
  transactions: []
}

export const mutations = {
  SET_PAYMENTS(state, payments) {
    state.payments = payments.data
  },
  SET_PAYMENT(state, payment) {
    state.payment = payment
  },
  SET_TRANSACTIONS(state, transactions) {
    state.transactions = transactions
  },
  SET_TRANSACTION_COLLECTIONS(state, params) {
    let transaction = state.transactions.find(transaction => transaction.id === params.transactionId)

    if (transaction) {
      transaction.sub = params.collections
    }
  },
  DELETE_TRANSACTION(state, transactionId) {
    state.transactions = state.transactions.filter(transaction => transaction.id !== transactionId)
  }
}

export const actions = {
  getPayments({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      PaymentService.getPayments(id)
        .then(response => {
          commit('SET_PAYMENTS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.get_payments_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getPayment({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      PaymentService.getPayment(params.orderId, params.id)
        .then(response => {
          commit('SET_PAYMENT', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.get_payment_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getTransactions({ commit, dispatch }, orderId) {
    return new Promise((resolve, reject) => {
      PaymentService.getTransactions(orderId)
        .then(response => {
          commit('SET_TRANSACTIONS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.get_transactions_error') + ' ' + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteTransaction({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      PaymentService.deleteTransaction(params.orderId, params.transactionId, params.data)
        .then(response => {
          commit('DELETE_TRANSACTION', params.transactionId)

          dispatch(
            'notification/add',
            {
              type: 'success',
              message: t('store.admin.payment.delete_transaction_success')
            },
            { root: true }
          )

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${t('store.admin.payment.delete_transaction_error')} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getTransactionCollections({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      PaymentService.getTransactionCollections(params.orderId, params.transactionId)
        .then(response => {
          commit('SET_TRANSACTION_COLLECTIONS', {
            transactionId: params.transactionId,
            collections: response.data
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.get_collections_by_transaction_error') + ' ' + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  setTransactionCollection({ commit }, { transactionId, collections }) {
    return new Promise((resolve) => {
      commit('SET_TRANSACTION_COLLECTIONS', {
        transactionId: transactionId,
        collections: collections
      })
      resolve(true)
    })
  },
  toggleImportFinanceInfoModal({ dispatch }) {
    eventEmitter.emit('toggle-import-finance-info-modal-event')
  },
  toggleImportPaymentInfoModal({ dispatch }) {
    eventEmitter.emit('toggle-import-payment-info-modal-event')
  },
  toggleImportPaymentsModal({ dispatch }) {
    eventEmitter.emit('toggle-import-payments-modal-event')
  },
  toggleImportCollectionModal({ dispatch }) {
    eventEmitter.emit('toggle-import-collection-modal-event')
  },
  startImportFinanceInfoProcess({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      PaymentService.startImportFinanceInfoProcess(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.payment.start_import_finance_info_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.start_import_finance_info_process_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  startImportPaymentInfoProcess({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      PaymentService.startImportPaymentInfoProcess(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.payment.start_import_payment_info_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.start_import_payment_info_process_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  startImportRetailerPaymentsProcess({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      PaymentService.startImportRetailerPaymentsProcess(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.payment.start_import_retailer_payments_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.start_import_retailer_payments_process_error')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  startImportCollectionProcess({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      PaymentService.startImportCollectionProcess(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.payment.start_import_collection_process_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.payment.start_import_collection_process_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  sendLateFeeSms({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      PaymentService.sendLateFeeSms(params.payment_id, params)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.payment.send_late_fee_sms_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${t('store.admin.payment.send_late_fee_sms_fail')} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
